import React, { useRef, useState, useEffect, useMemo } from "react";
import "./tenant-basic-details.scss";
import { Box, Button } from "@mui/material";
import EqualWidthList from "../../../../../common/components/equal-width-list/EqualWidthList";
import EditBasicDetails from "./edit-basic-details/edit-basic-details";
import EditBasicAddress from "./edit-basic-address/edit-basic-address";
import { AlertComponent } from "../../../../../common/components";
import {
  AlertMessage,
  handleAddressInitialValues,
} from "../../tenant-details-types";
import { useResourcePermissions } from "../../../../../common/hooks/resource-permissions";
import MODULES from "../../../../../constants/modules";
import { getLinkedPartnerStatus } from "../../../../../api/tenets";
import { LinkPartnerPopup } from "./linkPartnerPopup";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import { isValidPhoneNumber } from "../../../../../utils/utils";

interface TenantUserDetailsProps {
  tenantData: any;
  setTenantData: any;
}

const TenantBesicDetails: React.FC<TenantUserDetailsProps> = ({
  tenantData,
  setTenantData,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isBillingAddressEditing, setIsBillingAddressEditing] = useState(false);
  const billingAddressFormRef = useRef<any>(null);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [linkedPartner, setLinkedPartner] = useState<any>(null);
  const [isLinkPartnerModalOpen, setIsLinkPartnerModalOpen] = useState(false);

  const fetchLinkedPartnerStatus = async () => {
    try {
      setIsLoading(true);
      const res = await getLinkedPartnerStatus(tenantData.id);
      setLinkedPartner(res.data.data);
    } catch (err) {
      setAlertMessage({
        open: true,
        message:
          (err as any)?.response?.data?.message ||
          "Failed to fetch linked partner status",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!tenantData?.id) return;
    fetchLinkedPartnerStatus();
  }, [tenantData?.id]);

  const handleOpenLinkPartnerModal = () => setIsLinkPartnerModalOpen(true);

  const handleCancel = () => {
    setIsEditing(false);
  };

  const { canWrite } = useResourcePermissions(MODULES.PARTNER_LIST);

  const handleError = (err: any, msg?: string) => {
    setAlertMessage({
      open: true,
      message: (err as any)?.response?.data?.message || msg,
      type: "error",
    });
  };

  const handleApiSuccuss = (successMsg: string) => {
    setAlertMessage({
      open: true,
      message: successMsg,
      type: "success",
    });
  };

  const handleCompanyAddressAPISuccess = (res: any) => {
    setTenantData((prev: any) => ({
      ...prev,
      billing_address: res.billing_address,
    }));
    handleApiSuccuss("Address updated successfully");
    setIsBillingAddressEditing(false);
  };

  const handleBasicDetailsAPISuccess = () => {
    handleApiSuccuss("Basic details updated successfully");
    setIsEditing(false);
  };

  const superAdmin = tenantData?.users?.find(
    (user: any) => user?.is_primary_user,
  );

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Box>
      <Box className="tenant-basic-details-wrapper">
        {isEditing ? (
          <>
            <EditBasicDetails
              handleCancel={handleCancel}
              tenantData={tenantData}
              handleError={handleError}
              setTenantData={setTenantData}
              handleAPIsuccess={handleBasicDetailsAPISuccess}
            />
          </>
        ) : (
          <>
            <Box className="tenant-basic-details-header">
              {canWrite && (
                <Button
                  variant="outlined"
                  className="tenant-basic-details-header-button"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  Edit Details
                </Button>
              )}
            </Box>
            <Box className="tenant-basic-details-content" gap={3}>
              <Box className="tenant-basic-details-content-header">
                <EqualWidthList
                  labelValue={superAdmin?.first_name || "-"}
                  label="First Name"
                />
                <EqualWidthList
                  labelValue={superAdmin?.last_name || "-"}
                  label="Last Name"
                />
                <EqualWidthList
                  labelValue={superAdmin?.email || "-"}
                  label="Email Address"
                  type="Email"
                />
                <EqualWidthList
                  labelValue={
                    isValidPhoneNumber(superAdmin?.phone_number)
                      ? superAdmin?.phone_number
                      : "-"
                  }
                  label="Phone"
                  type="Phone"
                />
                <EqualWidthList
                  labelValue={
                    tenantData?.basic_details?.products?.length
                      ? tenantData?.basic_details?.products.join(", ")
                      : "-"
                  }
                  label="Products"
                />
              </Box>
              <Box className="tenant-basic-details-content-header">
                <EqualWidthList
                  labelValue={tenantData?.basic_details?.company_name || "-"}
                  label="Company Name"
                />
                <EqualWidthList
                  labelValue={tenantData?.basic_details?.company_website || "-"}
                  label="Company Website"
                  type="website"
                />
                <EqualWidthList
                  labelValue={tenantData?.basic_details?.timezone || "-"}
                  label="Time Zone"
                />
                <EqualWidthList
                  labelValue={tenantData?.basic_details?.employee_count || "-"}
                  label="Employee Count"
                />
                <EqualWidthList
                  labelValue={
                    tenantData?.basic_details?.email?.toString() || "-"
                  }
                  label="Company Email"
                  type="Email"
                />
                <EqualWidthList
                  labelValue={
                    isValidPhoneNumber(tenantData?.basic_details?.phone_number)
                      ? tenantData?.basic_details?.phone_number
                      : "-"
                  }
                  label="Company Phone"
                  type="Phone"
                />
              </Box>
            </Box>
          </>
        )}
      </Box>

      <Box
        className="tenant-basic-details-wrapper"
        sx={{ borderRadius: "10px" }}
      >
        <Box className="tenant-basic-details-header tenant-basic-details-address ">
          <p>Billing Address</p>
          <Box>
            {isBillingAddressEditing ? (
              <Box sx={{ display: "flex", marginRight: "-8px" }}>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => {
                    setIsBillingAddressEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    billingAddressFormRef.current?.submitForm();
                  }}
                  className="cancel-button next-button-wrapper"
                  //   disabled={!(isFormDirty || uploadedImageFile)}
                >
                  Save
                </button>
              </Box>
            ) : (
              <>
                {canWrite && (
                  <Button
                    variant="outlined"
                    className="tenant-basic-details-header-button"
                    onClick={() => setIsBillingAddressEditing(true)}
                  >
                    {isBillingAddressEditing ? "Save" : "Edit"}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
        {isBillingAddressEditing ? (
          <Box className="tenant-basic-details-right-panel tenant-details-left-panel-address-edit">
            <EditBasicAddress
              data={tenantData}
              formRef={billingAddressFormRef}
              type="billing"
              handleSuccessApi={handleCompanyAddressAPISuccess}
              initialValues={{
                ...tenantData?.billing_address,
                streetLine1: tenantData?.billing_address?.street_line_1 || "",
                streetLine2: tenantData?.billing_address?.street_line_2 || "",
              }}
              handleError={handleError}
            />
          </Box>
        ) : (
          <Box className="tenant-basic-details-content" gap={3}>
            <Box className="tenant-basic-details-content-header">
              <EqualWidthList
                label="Street Line 1"
                labelValue={tenantData?.billing_address?.street_line_1 || "-"}
              />
              <EqualWidthList
                label="Street Line 2"
                labelValue={tenantData?.billing_address?.street_line_2 || "-"}
              />
              <EqualWidthList
                label="State"
                labelValue={tenantData?.billing_address?.state || "-"}
              />
            </Box>

            <Box className="tenant-basic-details-content-header">
              <EqualWidthList
                label="Country"
                labelValue={tenantData?.billing_address?.country || "-"}
              />
              <EqualWidthList
                label="City"
                labelValue={tenantData?.billing_address?.city || "-"}
              />
              <EqualWidthList
                label="Zip Code"
                labelValue={tenantData?.billing_address?.postcode || "-"}
              />
            </Box>
          </Box>
        )}
      </Box>
      <Box
        className="tenant-basic-details-wrapper"
        sx={{ borderRadius: "10px" }}
      >
        <Box className="linked-partner-header">
          <p>Linked Partner</p>
        </Box>
        <Box className="tenant-basic-details-content" gap={3}>
          <Box className="tenant-basic-details-content-header">
            {linkedPartner?.linked ? (
              <div className="link-partner-item">
                <EqualWidthList
                  labelValue={linkedPartner.partner.businessName}
                  label={`${linkedPartner?.partner.businessType === "Preneed Planner" ? "MemorialGo Preneed Planner Name:" : "MemorialGo Provider Name:"} `}
                />
              </div>
            ) : (
              <div className="link-partner-item">
                <span className="item-label">Linked Partner</span>
                <span
                  className="item-value-link"
                  onClick={handleOpenLinkPartnerModal}
                >
                  Link Partner
                </span>
              </div>
            )}
          </Box>
        </Box>
      </Box>
      <LinkPartnerPopup
        isOpen={isLinkPartnerModalOpen}
        setIsOpen={setIsLinkPartnerModalOpen}
        tenantId={tenantData?.id}
        setIsLoading={setIsLoading}
        setAlertMessage={setAlertMessage}
        onPartnerLinked={fetchLinkedPartnerStatus}
      />
      {alertMessage.open && (
        <AlertComponent
          message={alertMessage.message}
          type={alertMessage.type || "info"}
          open={alertMessage.open}
          onClose={() => setAlertMessage({ open: false })}
        />
      )}
    </Box>
  );
};

export default TenantBesicDetails;
